'use client';

import React, {
  Context,
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import NextImage from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { useMedia } from 'react-use';
import { Controller, useForm } from 'react-hook-form';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { LoaderCircle } from 'lucide-react';
import { z } from 'zod';


import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { Tabs, TabsContent } from 'components/ui/tabs';
import { Input } from 'components/ui/input';
import { Sheet, SheetContent } from 'components/ui/sheet';
import { InfoIcon } from 'components/icons';

import { MEMBER_ROUTES, OWNER_ROUTES } from 'utils/routes';

import {
  ApplicationFieldNode,
  OAuthLink,
  RoleEnum,
} from 'generated/types';
import { useLoginUserMutation } from 'generated/hooks';
import { useFormErrorHandling } from 'hooks/useFormErrorHandling';
import { useCommunityLandingPageTracking } from 'app/_tracking/events';
import { GetOAuthButton } from '../(unauthenticated)/_utils/oauth';

import { CurrentUserType } from 'types';
import ForgotPassword from 'app/(unauthenticated)/forgot-password/forgotPassword';
import { ArrowLeftIcon } from 'components/icons/v2';
import { ApplyForm } from 'app/(unauthenticated)/apply/ApplyForm';
import { CommunityInfoQuery } from 'generated/operations';
import { getLandingConfig } from 'utils/landing';
import { useAuth } from 'contexts/auth';
import Link from 'next/link';

interface IApplyLoginProvider {
  children: ReactNode;

  currentUser: CurrentUserType;
  community: Pick<NonNullable<CommunityInfoQuery['communityInfo']>,
    'id' | 'name' | 'logo' | 'subdomain' | 'joinPolicy' | 'displayLandingApplication'
  >;
  landingFields: ApplicationFieldNode[];
  oAuthLinks: OAuthLink[];
  applyAfterLogin?: boolean;
  loginHint?: string
}

export enum ModalTabs {
  LOGIN = 'login',
  APPLY = 'apply'
}

enum ForgotPasswordState {
  NOT_SEND = 'not_send',
  SENT = 'sent'
}

interface ILoginForm {
  email: string;
  password: string;
}

const loginFormSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
  password: z.string().min(1, 'Password cannot be empty')
});


interface IApplyLogin {
  setApplyTab: Dispatch<SetStateAction<ModalTabs | undefined>>;
  applyOrLogin: () => void;
}

const defaultContext: IApplyLogin = {
  setApplyTab: () => console.error('ApplyLoginContext is not initialized'),
  applyOrLogin: () => console.error('ApplyLoginContext is not initialized')
};

const ApplyLoginContext: Context<IApplyLogin> = createContext(defaultContext);

export const ApplyLoginProvider: FC<IApplyLoginProvider> = (props) => {
  const {
    currentUser,
    children,
    community,
    landingFields,
    oAuthLinks,
    applyAfterLogin = false,
    loginHint,
  } = props;

  const isMobile = useMedia('(max-width: 768px)', false);
  const params = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const { currentUser: clientCurrentUser, reFetchCurrentUser } = useAuth();
  const { handleErrors } = useFormErrorHandling();
  const { trackCommunityLandingPage } = useCommunityLandingPageTracking();
  const [loginState, loginMutation] = useLoginUserMutation();

  const [currentTab, setTab] = useState<ModalTabs | undefined>();
  const [forgotPasswordState, setForgotPasswordState] = useState<ForgotPasswordState | undefined>();
  const [socialError, setSocialError] = useState<string>('');

  const appConfig = getLandingConfig(community);

  useEffect(() => {
    trackCommunityLandingPage();
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError
  } = useForm<ILoginForm>({
    mode: 'all',
    resolver: standardSchemaResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const applyOrLogin = useCallback(() => {
    if (!currentUser && !clientCurrentUser) {
      setTab(ModalTabs.LOGIN);
    } else {
      setTab(ModalTabs.APPLY);
    }
  }, [clientCurrentUser, currentUser]);

  const value: IApplyLogin = useMemo(() => ({
    currentTab,
    setApplyTab: setTab,
    applyOrLogin
  }), [currentTab, applyOrLogin]);

  const onSubmitLogin = useCallback(async (data: any) => {
    try {
      const vars = {
        email: data.email,
        password: data.password
      };
      const context = {
        fetchOptions: { headers: { 'X-Community-Subdomain': community?.subdomain ?? '' } }
      };
      const resp = await loginMutation(vars, context);

      if (resp.error) {
        return handleErrors<ILoginForm>(resp.error, setError, { username: 'email' });
      }

      if (applyAfterLogin) {
        reFetchCurrentUser();
        setTab(ModalTabs.APPLY);
        return;
      }

      if (!resp.data?.login?.user?.communityMember || resp.data.login.user.communityMember.role === RoleEnum.Member) {
        return router.push(MEMBER_ROUTES.MEMBERS.getPath());
      }

      if (!resp.data?.login?.community?.id) {
        return router.push(OWNER_ROUTES.START.getPath());
      }

      if (resp.data?.login?.user?.id && resp.data?.login?.community) {
        let next = OWNER_ROUTES.PANEL.getPath();
        if (params?.get('redirect')) {
          next = params?.get('redirect') || '';
        }

        router.push(next);
      }
    } catch (error) {
      console.error(error);
    }
  }, [community?.subdomain, loginMutation, handleErrors, setError, router, params]);

  const handleCloseLoginModal = useCallback(() => {
    setSocialError(() => '');
    setTab(() => undefined);
  }, []);

  useEffect(() => {
    reset();
  }, [currentTab, reset]);

  useEffect(() => {
    if (params?.size) {
      let restParams = params.toString();

      if (!!params?.get('socialError') && params?.get('socialError') === 'not_admin') {
        setSocialError(() => params.get('socialError') ?? '');

        restParams = restParams.replace('socialError=not_admin', '');
      }

      router.replace(`${OWNER_ROUTES.HOME.getPath()}?${restParams}`);
    }
  }, [params, router]);

  const DialogRoot = isMobile ? Sheet : Dialog;
  const Content = isMobile ? SheetContent : DialogContent;

  return (
    <ApplyLoginContext.Provider value={value}>
      {children}
      <DialogRoot open={!!currentTab} onOpenChange={handleCloseLoginModal}>
        <Content
          showClose
          className="p-0 bg-white md:!max-w-full md:!w-full md:h-full"
          side="top"
        >
          <Tabs defaultValue={currentTab} value={currentTab}
                onValueChange={(value) => setTab(() => value as ModalTabs)}>
            <TabsContent value={ModalTabs.APPLY}>
              <ApplyForm
                landingFields={landingFields}
                community={community}
              />
            </TabsContent>
            <TabsContent value={ModalTabs.LOGIN}>
              <div className='p-6xl md:p-3 sm:p-0'>
                <div className='px-4xl md:p-4'>
                  <div className='flex flex-col gap-3xl'>
                    <div className='flex gap-2 self-center items-center'>
                      {!!community?.logo && (
                        <NextImage src={community.logo} width={41} height={41} alt='Community-logo' />
                      )}
                    </div>
                    <div className='flex flex-col gap-lg items-center'>
                      <p className='text-black text-display-sm font-semibold'>{community?.name}</p>
                      {currentTab === ModalTabs.APPLY && (
                        <p className='text-gray-400 text-medium'>Fill in the Application</p>
                      )}
                      {currentTab === ModalTabs.LOGIN && loginHint && (
                        <p className='text-gray-400 text-medium'>{loginHint}</p>
                      )}
                    </div>
                    <div>
                      <div className='pt-4xl flex flex-col gap-6'>
                        <div className='flex flex-col gap-3'>
                          {socialError === 'not_admin' && (
                            <div className='mt-3'>
                              <Alert className='bg-red-light text-red-600'>
                                <div className='flex gap-3 items-center'>
                                  <InfoIcon />
                                  No permissions to access community
                                </div>
                              </Alert>
                            </div>
                          )}
                          {oAuthLinks?.map((link, index) => (
                            <GetOAuthButton link={link} key={index} />
                          ))}
                        </div>
                        <div className='flex items-center gap-2'>
                          <div className='flex-1 border-b border-b-gray-200' />
                          <span className='text-medium text-gray-600'>OR</span>
                          <div className='flex-1 border-b border-b-gray-200' />
                        </div>
                        <form className='flex flex-col gap-5' onSubmit={handleSubmit(onSubmitLogin)}>
                          <Controller
                            control={control}
                            name='email'
                            render={({ field, formState: { errors } }) => (
                              <Input
                                label='Email'
                                placeholder='Enter your email'
                                errorText={errors.email?.message as string}
                                {...field}
                              />
                            )}
                          />
                          <Controller
                            control={control}
                            name='password'
                            render={({ field }) => (
                              <Input
                                label='Password'
                                type='password'
                                placeholder='••••••••'
                                errorText={errors.password?.message as string}
                                {...field}
                              />
                            )}
                          />
                          {!!errors.root && (
                            <p
                              className={`text-small text-error-600`}>{errors.root.message}</p>
                          )}
                          <div className='flex justify-end'>
                            <Button
                              type='button'
                              variant='nakedGrayLink'
                              className='p-0 text-small'
                              onClick={() => {
                                handleCloseLoginModal();
                                setForgotPasswordState(() => ForgotPasswordState.NOT_SEND);
                              }}
                            >
                              Forgot password
                            </Button>
                          </div>
                          <Button
                            disabled={loginState.fetching}
                            type='submit'
                            variant={appConfig.buttonVariant}
                            size='large'
                          >
                            {loginState.fetching && <LoaderCircle className='animate-spin' />}
                            Sign in
                          </Button>
                        </form>
                        <div className='flex justify-center items-center gap-1'>
                          <p className='text-sm text-gray-600'>Don’t have an account?</p>
                          <Link href={`${OWNER_ROUTES.REGISTER.getPath()}?redirect=${pathname}`}>
                            <Button
                              className={`p-0 text-sm ${appConfig.joinButton}`}
                              size='medium'
                              variant='nakedGrayLink'
                              title={'Sign up'}
                            >
                              Sign up
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </Content>
      </DialogRoot>
      {!!forgotPasswordState && (
        <Dialog open onOpenChange={() => setForgotPasswordState(() => undefined)}>
          <DialogContent className="p-0 sm-1/2:max-w-full sm-1/2:w-full">
            <div className="p-6xl md:p-8">
              <div className='flex flex-col gap-8'>
                <ForgotPassword
                  onSuccess={() => {
                    setForgotPasswordState(() => ForgotPasswordState.SENT);
                  }}
                />
                <Button
                  type='button'
                  variant='nakedGrayLink'
                  className='p-0 self-center'
                  onClick={() => {
                    setForgotPasswordState(() => undefined);
                    setTab(() => ModalTabs.LOGIN);
                  }}
                >
                  <ArrowLeftIcon height={20} width={20} />
                  Back to sign in
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </ApplyLoginContext.Provider>
  );
};

export default ApplyLoginContext;
